import React from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  Megaphone,
  Users,
  TrendingUp,
  Calendar as CalendarIcon,
  CheckCircle2,
  ArrowUpRight,
  Instagram,
  Facebook,
  Linkedin,
  Music2,
  Camera,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SafeJsonLd from '@/components/SafeJsonLd';
import { useLanguage } from '@/contexts/LanguageContext';
import ServicePricing, { PricingTier } from '@/components/service/ServicePricing';
import ServiceFAQ, { FAQItem } from '@/components/service/ServiceFAQ';
import ServiceCrossSell from '@/components/service/ServiceCrossSell';
import ServiceBreadcrumb from '@/components/service/ServiceBreadcrumb';

const ServicesSocial: React.FC = () => {
  const { t, language } = useLanguage();
  const isPT = language === 'pt';

  const features = [
    { Icon: Users,         key: 'content' },
    { Icon: TrendingUp,    key: 'growth' },
    { Icon: CalendarIcon,  key: 'planning' },
  ] as const;

  const benefitsRaw = t('servicesSocial.benefits') as unknown;
  const benefits: string[] = Array.isArray(benefitsRaw) ? (benefitsRaw as string[]) : [];

  const title = isPT
    ? 'Gestão de Redes Sociais para Pequenas Empresas | Spawncore'
    : 'Social Media Management for Small Businesses | Spawncore';

  const description = isPT
    ? 'Gestão completa de Facebook, Instagram, LinkedIn e TikTok para pequenas empresas em Portugal. Criação de conteúdo, copywriting, agendamento e relatórios mensais.'
    : 'Full Facebook, Instagram, LinkedIn and TikTok management for small businesses in Portugal. Content creation, copywriting, scheduling and monthly reports.';

  const tiers: PricingTier[] = isPT ? [
    {
      name: 'Light',
      price: '€200',
      period: '/mês',
      description: 'Presença consistente para começar.',
      features: [
        '8 publicações/mês (FB + IG)',
        'Criação visual com a sua marca',
        'Copywriting em português PT',
        'Calendário editorial mensal',
        'Agendamento via Meta Business',
        'Relatório mensal simples',
      ],
      ctaLabel: 'Começar Light',
    },
    {
      name: 'Brand',
      price: '€420',
      period: '/mês',
      description: 'Marca ativa, stories e engagement.',
      features: [
        '16 publicações/mês (FB + IG)',
        '12 stories/semana',
        '2 reels/mês com edição',
        'Resposta a comentários (até 3 dias/sem)',
        'Hashtag research e otimização',
        'Estratégia trimestral',
        'Relatório detalhado mensal',
      ],
      popular: true,
      ctaLabel: 'Escolher Brand',
    },
    {
      name: 'Scale',
      price: '€850+',
      period: '/mês',
      description: 'Conteúdo + ads + atendimento de DMs.',
      features: [
        'Conteúdo Brand completo',
        'Adicionar LinkedIn ou TikTok',
        'Gestão de DMs e mensagens',
        'Campanhas Meta Ads (até €500 gasto)',
        'Sessão fotográfica trimestral',
        'Influencer outreach básico',
        'Reuniões mensais de estratégia',
      ],
      ctaLabel: 'Falar com o estúdio',
    },
  ] : [
    {
      name: 'Light',
      price: '€200',
      period: '/mo',
      description: 'Consistent presence to get started.',
      features: [
        '8 posts/month (FB + IG)',
        'Visual creation in your brand',
        'PT/EN copywriting',
        'Monthly editorial calendar',
        'Scheduling via Meta Business',
        'Simple monthly report',
      ],
      ctaLabel: 'Start Light',
    },
    {
      name: 'Brand',
      price: '€420',
      period: '/mo',
      description: 'Active brand, stories and engagement.',
      features: [
        '16 posts/month (FB + IG)',
        '12 stories/week',
        '2 reels/month with editing',
        'Comment replies (up to 3 days/wk)',
        'Hashtag research and optimisation',
        'Quarterly strategy',
        'Detailed monthly report',
      ],
      popular: true,
      ctaLabel: 'Choose Brand',
    },
    {
      name: 'Scale',
      price: '€850+',
      period: '/mo',
      description: 'Content + ads + DM handling.',
      features: [
        'Full Brand content',
        'Add LinkedIn or TikTok',
        'DMs and inbox management',
        'Meta Ads campaigns (up to €500 spend)',
        'Quarterly photo session',
        'Basic influencer outreach',
        'Monthly strategy meetings',
      ],
      ctaLabel: 'Talk to the studio',
    },
  ];

  const platforms = [
    { Icon: Instagram, name: 'Instagram', desc: isPT ? 'Feed, stories, reels'   : 'Feed, stories, reels' },
    { Icon: Facebook,  name: 'Facebook',  desc: isPT ? 'Posts, eventos, Ads'    : 'Posts, events, Ads' },
    { Icon: Linkedin,  name: 'LinkedIn',  desc: isPT ? 'B2B & autoridade'       : 'B2B & authority' },
    { Icon: Music2,    name: 'TikTok',    desc: isPT ? 'Vídeo curto, alcance'   : 'Short video, reach' },
    { Icon: Camera,    name: 'Pinterest', desc: isPT ? 'Tráfego para o site'    : 'Drive site traffic' },
    { Icon: Megaphone, name: 'Meta Ads',  desc: isPT ? 'Campanhas pagas'        : 'Paid campaigns' },
  ];

  const useCases = isPT ? [
    { title: 'Restaurantes',     desc: 'Fotos de pratos, menus do dia, reviews de clientes, eventos especiais.' },
    { title: 'Clínicas & saúde', desc: 'Conteúdo educativo, dicas, equipa, testemunhos com rigor RGPD.' },
    { title: 'Retalho local',    desc: 'Novidades, promoções, lifestyle e geração de tráfego para a loja.' },
    { title: 'Imobiliárias',     desc: 'Imóveis em destaque, vídeos curtos, mercado local, equipa.' },
    { title: 'Serviços B2B',     desc: 'Casos de estudo, posicionamento técnico, LinkedIn-first.' },
    { title: 'E-commerce',       desc: 'Produtos novos, ofertas, social commerce, anúncios pagos.' },
  ] : [
    { title: 'Restaurants',     desc: 'Food photos, daily menus, reviews, special events.' },
    { title: 'Clinics & health',desc: 'Educational content, tips, team, GDPR-careful testimonials.' },
    { title: 'Local retail',    desc: 'New arrivals, promotions, lifestyle, foot traffic.' },
    { title: 'Real estate',     desc: 'Featured listings, short videos, local market, team.' },
    { title: 'B2B services',    desc: 'Case studies, technical positioning, LinkedIn-first.' },
    { title: 'E-commerce',      desc: 'New products, offers, social commerce, paid ads.' },
  ];

  const faqs: FAQItem[] = isPT ? [
    {
      question: 'Quantas publicações por mês?',
      answer: 'Depende do plano: Light tem 8 publicações/mês (2 por semana, em FB e IG sincronizadas). Brand sobe para 16/mês, incluindo stories e reels. Scale acrescenta DMs, ads e plataformas extras. Sempre adaptado ao seu setor e calendário.',
    },
    {
      question: 'Tratam dos comentários e mensagens?',
      answer: 'Nos planos Brand e Scale sim. Brand inclui resposta a comentários (3 dias/semana). Scale acrescenta gestão de DMs com SLAs definidos. No Light pode ativar como add-on.',
    },
    {
      question: 'Fazem anúncios pagos no Facebook e Instagram?',
      answer: 'Sim — incluído no plano Scale (até €500/mês de gasto). Em planos Light/Brand pode adicionar Meta Ads como serviço extra. Cobramos um fee de gestão (15%) e damos sempre acesso total à sua conta Business — o dinheiro é seu, a conta é sua.',
    },
    {
      question: 'Quem cria o conteúdo visual?',
      answer: 'A nossa equipa. Trabalhamos com fotos suas, banco de imagens premium ou — no plano Scale — sessões trimestrais de fotos. Para vídeo curto (reels/TikTok) edição básica está incluída. Filmagens em local são orçamentadas à parte.',
    },
    {
      question: 'Conseguem escrever em português correto?',
      answer: 'Sim, escrevemos em português europeu nativo, com tom adaptado à sua marca (formal, casual, técnico, divertido). Bilingue PT/EN sem custo extra para mercados internacionais.',
    },
    {
      question: 'Vou ver os posts antes de serem publicados?',
      answer: 'Sim. Enviamos o calendário editorial do mês para aprovação. Tem 5 dias para sugerir alterações. Após aprovação publicamos automaticamente nas datas combinadas. Sem surpresas.',
    },
    {
      question: 'Há fidelização?',
      answer: 'Não. Os planos são mensais e pode cancelar com 30 dias de aviso. Acreditamos que se o serviço for bom, você fica.',
    },
  ] : [
    {
      question: 'How many posts per month?',
      answer: 'Depends on the plan: Light is 8 posts/month (2 per week, FB and IG synced). Brand goes up to 16/month including stories and reels. Scale adds DMs, ads and extra platforms. Always tailored to your sector and calendar.',
    },
    {
      question: 'Do you handle comments and DMs?',
      answer: 'On Brand and Scale, yes. Brand includes comment replies (3 days/week). Scale adds DM handling with defined SLAs. On Light you can add it as an extra.',
    },
    {
      question: 'Do you run paid Facebook and Instagram ads?',
      answer: 'Yes — included on Scale (up to €500/month spend). On Light/Brand you can add Meta Ads as an extra service. We charge a 15% management fee and you always keep full Business account access — the money is yours, the account is yours.',
    },
    {
      question: 'Who creates the visual content?',
      answer: 'Our team. We work with your own photos, premium stock, or — on Scale — quarterly photo sessions. Basic short-video editing (reels/TikTok) is included. On-location shoots are quoted separately.',
    },
    {
      question: 'Do you write proper Portuguese?',
      answer: 'Yes, native European Portuguese, tone-adapted to your brand (formal, casual, technical, fun). Bilingual PT/EN at no extra cost for international markets.',
    },
    {
      question: 'Do I see posts before they go live?',
      answer: 'Yes. We send the monthly editorial calendar for approval. You have 5 days to suggest changes. Once approved we auto-publish on agreed dates. No surprises.',
    },
    {
      question: 'Is there a lock-in?',
      answer: 'No. Monthly plans, cancel with 30 days notice. We believe if the service is good, you stay.',
    },
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Social Media Management',
    name: isPT ? 'Gestão de Redes Sociais' : 'Social Media Management',
    description,
    provider: { '@type': 'Organization', name: 'Spawncore', url: 'https://spawncore.com' },
    areaServed: 'PT',
    audience: { '@type': 'BusinessAudience', audienceType: 'Small Business' },
    url: 'https://spawncore.com/services/social',
    offers: tiers.map(t => ({
      '@type': 'Offer',
      name: t.name,
      price: t.price.replace(/[^\d]/g, '') || undefined,
      priceCurrency: 'EUR',
      description: t.description,
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={isPT ? 'pt' : 'en'} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={isPT
          ? 'gestão redes sociais Portugal, social media manager Azambuja, Instagram pequenas empresas, Facebook negócios, gestão Instagram, criação conteúdo social, Meta Ads Portugal, TikTok marketing, LinkedIn B2B, marketing redes sociais'
          : 'social media management Portugal, Instagram small business, Facebook for business, Instagram management, social content creation, Meta Ads Portugal, TikTok marketing, LinkedIn B2B, social media marketing'} />
        <link rel="canonical" href="https://spawncore.com/services/social" />
        <link rel="alternate" hrefLang="pt-PT" href="https://spawncore.com/services/social" />
        <link rel="alternate" hrefLang="en" href="https://spawncore.com/services/social" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spawncore.com/services/social" />
        <meta property="og:image" content="https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png" />
        <meta property="og:locale" content={isPT ? 'pt_PT' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <SafeJsonLd data={serviceSchema} />

      <header>
        <Navigation />
      </header>

      <ServiceBreadcrumb label={t('nav.social')} url="https://spawncore.com/services/social" />

      <main>
        {/* Hero */}
        <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 aurora-bg opacity-60" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />
          <div className="orb orb-violet top-[10%] left-[10%] h-64 w-64" aria-hidden="true" />
          <div className="orb orb-pink   top-[40%] right-[8%] h-56 w-56" aria-hidden="true" />

          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <span className="pill-tag mb-8">
              <Megaphone className="h-3 w-3" />
              {isPT ? 'Redes Sociais · Para pequenas empresas' : 'Social Media · For small business'}
            </span>
            <h1 className="font-display text-foreground text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.96] tracking-tight">
              {isPT ? 'Conteúdo que ' : 'Content that '}
              <span className="italic font-display gradient-text-shimmer">
                {isPT ? 'constrói marca' : 'builds a brand'}
              </span>
              {isPT ? '. Todos os dias.' : '. Every day.'}
            </h1>
            <p className="mt-7 text-lg md:text-2xl text-muted-foreground max-w-2xl leading-relaxed">
              {t('servicesSocial.subtitle')}
            </p>
            <p className="mt-5 text-base text-muted-foreground/80 max-w-2xl leading-relaxed">
              {isPT
                ? 'Facebook, Instagram, LinkedIn e TikTok geridos pela mesma equipa. Calendário editorial, copy em português PT, edição de reels, ads e relatórios — sempre com a sua marca à frente.'
                : 'Facebook, Instagram, LinkedIn and TikTok managed by one team. Editorial calendar, native PT copy, reel editing, ads and reports — your brand always first.'}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="group h-14 px-7 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
                >
                  <Megaphone className="mr-2 h-4 w-4" />
                  {t('servicesSocial.cta')}
                  <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <a href="#pricing" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-7 rounded-full bg-card/40 border-border hover:bg-card text-base w-full sm:w-auto"
                >
                  {isPT ? 'Ver preços' : 'See pricing'}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {t('servicesSocial.howWeHelp')}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'O que entregamos, todos os meses.' : 'What we deliver, every month.'}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {features.map(({ Icon, key }, idx) => (
                <article
                  key={key}
                  className="group relative overflow-hidden rounded-2xl border border-border/80 bg-card/70 backdrop-blur-sm p-8 transition-all duration-500 hover:bg-card hover:border-primary/40 hover:-translate-y-1"
                >
                  <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-primary/[0.10] blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-secondary text-primary">
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </div>
                      <span className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                        / 0{idx + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-[1.625rem] leading-tight tracking-tight mb-3 text-foreground">
                      {t(`servicesSocial.features.${key}.title`)}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {t(`servicesSocial.features.${key}.desc`)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="py-20 md:py-24 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <span className="eyebrow">— {isPT ? 'Plataformas' : 'Platforms'}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Onde a sua marca precisa de estar.' : 'Where your brand needs to be.'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border/60 rounded-2xl overflow-hidden border border-border/60">
              {platforms.map(({ Icon, name, desc }) => (
                <div key={name} className="bg-card/60 p-5 hover:bg-card transition-colors">
                  <Icon className="h-5 w-5 text-lime mb-3" strokeWidth={1.6} />
                  <div className="font-display text-sm text-foreground leading-tight">{name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-12 gap-6 mb-14">
              <div className="col-span-12 md:col-span-7">
                <span className="eyebrow">— {isPT ? 'Por setor' : 'By sector'}</span>
                <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                  {isPT ? 'O que funciona em cada nicho.' : 'What works in each niche.'}
                </h2>
              </div>
              <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-muted-foreground text-base md:text-lg leading-relaxed">
                {isPT ? 'Cada setor tem um ritmo e uma audiência. Adaptamos.' : 'Every sector has a rhythm and an audience. We adapt.'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCases.map((uc, idx) => (
                <article
                  key={uc.title}
                  className="group rounded-xl border border-border/80 bg-card/60 p-6 hover:bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70 mb-3">
                    / 0{idx + 1}
                  </div>
                  <h3 className="font-display text-xl text-foreground mb-2.5 leading-tight">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits / Included */}
        <section className="py-20 md:py-28 bg-background">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {t('servicesSocial.included')}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'O que está sempre incluído.' : 'What\'s always included.'}
              </h2>
            </div>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-5">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-4 group">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/[0.10] text-primary group-hover:bg-primary/[0.20] transition-colors">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <span className="text-foreground/90 text-base md:text-lg leading-relaxed pt-0.5">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Pricing */}
        <div id="pricing">
          <ServicePricing
            eyebrow={isPT ? 'Investimento' : 'Investment'}
            heading={isPT ? 'Planos mensais. Cancela quando quiser.' : 'Monthly plans. Cancel anytime.'}
            subtitle={isPT
              ? 'Os números são pontos de partida. O orçamento final ajusta-se ao volume real do seu negócio.'
              : 'These are starting points. Final pricing adjusts to your real business volume.'}
            tiers={tiers}
            note={isPT
              ? 'Inclui sempre acesso total à sua conta Meta Business. Conteúdo e contas são seus, para sempre.'
              : 'Always includes full access to your Meta Business account. Content and accounts are yours, forever.'}
          />
        </div>

        {/* FAQ */}
        <ServiceFAQ
          eyebrow={isPT ? 'Perguntas frequentes · Redes Sociais' : 'FAQ · Social Media'}
          heading={isPT ? 'Antes de assinar o plano.' : 'Before you sign up.'}
          subtitle={isPT ? 'Sem letras pequenas.' : 'No fine print.'}
          faqs={faqs}
        />

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-background relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 aurora-bg opacity-50" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <span className="pill-tag mb-6">
              <Megaphone className="h-3 w-3" />
              {isPT ? 'Chamada gratuita · 30 min' : 'Free 30-min call'}
            </span>
            <h2 className="font-display text-foreground text-[clamp(2rem,5vw,4rem)] leading-[1.02] tracking-tight mb-6">
              {t('servicesSocial.ready')}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('servicesSocial.readyText')}
            </p>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="group h-14 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
              >
                <Megaphone className="mr-2 h-4 w-4" />
                {t('servicesSocial.contact')}
                <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </section>

        <ServiceCrossSell current="social" />
      </main>

      <Footer />
    </div>
  );
};

export default ServicesSocial;
